import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { apiBaseUrl } from '../../config';
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import axios from 'axios';
import parse from "html-react-parser";

import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";

const SciencePage = () => {
  const isCarouselInitialized = useRef(false);
  const isDoctorCarouselInitialized = useRef(false);
  const [isCarouselReady, setIsCarouselReady] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState("collapseOne");
  const [cms, setCms] = useState([]);
  
  // Dynamic data states
  const [inputData, setInputData] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [scienceCards, setScienceCards] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  // Fetch CMS
  const fetchCms = async () => {
    try {
      const { data } = await axios.get(`${apiBaseUrl}/all-get-cms`);
  
      if (data.status) {
        const cmsData = {};
        data.cms_pages.forEach((item) => {
          cmsData[item.cms_id] = item;
        });
        setCms(cmsData);
      } else {
        setCms({});
      }
    } catch (err) {
      console.error("Error fetching CMS pages:", err);
      setCms({});
    }
  };

  // Fetch all items from database
  const fetchItems = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/items`, {
        params: { limit: 100 }
      });
      
      if (response.data.status) {
        const allItems = response.data.data;
        setInputData(allItems.filter(item => item.type === 'science_input'));
        setEvidence(allItems.filter(item => item.type === 'science_evidence'));
        setScienceCards(allItems.filter(item => item.type === 'science_card'));
        setComparisonData(allItems.filter(item => item.type === 'science_comparison'));
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  useEffect(() => {
    fetchCms();
    fetchItems();
  }, []);

  // Load carousel
  useEffect(() => {
    const loadCarousel = async () => {
      try {
        const jQueryModule = await import("jquery");
        const $ = jQueryModule.default || jQueryModule;
        window.jQuery = $;
        window.$ = $;
        await import("owl.carousel");
        setIsCarouselReady(true);
        setTimeout(() => {
          initializeCarousels();
        }, 500);
      } catch (error) {
        console.error("Error loading carousel:", error);
        setIsCarouselReady(false);
      }
    };

    const initializeCarousels = () => {
      const $ = window.$;
      if (!$ || !$.fn || !$.fn.owlCarousel) {
        console.error("jQuery or owlCarousel not available");
        return;
      }

      if (!isCarouselInitialized.current) {
        const testimonialElement = $(".testimonial-slider");
        if (testimonialElement.length) {
          try {
            testimonialElement.owlCarousel({
              items: 1,
              loop: true,
              autoplay: true,
              autoplayTimeout: 4000,
              smartSpeed: 800,
              dots: true,
              nav: false,
              margin: 30,
            });
            isCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing testimonial carousel:", e);
          }
        }
      }

      if (!isDoctorCarouselInitialized.current) {
        const doctorElement = $(".doctor-slider");
        if (doctorElement.length) {
          try {
            doctorElement.owlCarousel({
              loop: true,
              margin: 25,
              nav: false,
              dots: false,
              autoplay: true,
              autoplayTimeout: 3000,
              smartSpeed: 800,
              responsive: {
                0: { items: 1 },
                576: { items: 2 },
                992: { items: 3 },
                1200: { items: 4 },
              },
            });
            isDoctorCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing doctor carousel:", e);
          }
        }
      }
    };

    loadCarousel();

    return () => {
      const $ = window.$;
      if ($ && $.fn && $.fn.owlCarousel) {
        try {
          if (isCarouselInitialized.current) {
            $(".testimonial-slider").owlCarousel("destroy");
            isCarouselInitialized.current = false;
          }
          if (isDoctorCarouselInitialized.current) {
            $(".doctor-slider").owlCarousel("destroy");
            isDoctorCarouselInitialized.current = false;
          }
        } catch (e) {
          console.log("Carousel already destroyed or not initialized");
        }
      }
    };
  }, []);

  const toggleAccordion = (id) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  return (
    <div>
      <Helmet>
        <title>Science Of Clinical Intelligence</title>
      </Helmet>

      {/* ============================================ */}
      {/* HERO SECTION - CMS ID 55 */}
      {/* ============================================ */}
      <section className="hero-section" style={{ padding: "40px 0px" }}>
        <div className="container text-center">
          <h2 className="predyx-main-title mb-3" style={{ lineHeight: "43px" }}>
            {parse(cms[55]?.cms_page_heading || "Clinical Intelligence does not begin by inventing new biology. <span>It begins by understanding the biology we already know.</span>")}
          </h2>
          {parse(cms[55]?.cms_page_description || "")}
          <a href="#" className="action-items-primary-btn mr-2">
            {cms[55]?.cms_page_small_description || "Explore the Science"}
            <i className="fas fa-chevron-right" />
          </a>

          <img
            src={`${apiBaseUrl}/assets/images/${cms[55]?.cms_image}`}
            alt="About us"
            className="img-fluid"
            style={{ marginTop: "20px", borderRadius: "10px" }}
          />
        </div>
      </section>

      {/* ============================================ */}
      {/* CLINICAL INTELLIGENCE BEGINS WITH BIOLOGY - CMS ID 56 */}
      {/* ============================================ */}
      <section className="explain-section predyx-section">
        <div className="container">
          <div className="predyx-header text-center mb-4">
            <h2 className="predyx-main-title mt-2">
              {parse(cms[56]?.cms_page_heading || "Clinical Intelligence <span>Begins with Biology.</span>")}
            </h2>
          </div>
          <div className="predyx-content-box">
            {parse(cms[56]?.cms_page_description || "")}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOUR DATA TO UNDERSTANDING - CMS ID 57 */}
      {/* ============================================ */}
      <section className="predyx2-section">
        <div className="container">
          <div className="predyx2-heading text-center mb-4">
            <h2 className="predyx2-title mt-2">
              {parse(cms[57]?.cms_page_heading || "Four Data to <span>Understanding</span>")}
            </h2>
            <h4 className="predyx2-subtitle mt-2">
              {cms[57]?.cms_page_small_description || "Predyxiq follows a transparent sequence of clinical reasoning:"}
            </h4>
            <p className="predyx2-description mt-4">
              {cms[57]?.cms_page_description || "The Predyxiq Clinical Intelligence Algorithm is intentionally modality-agnostic. It integrates information from every major domain of routine healthcare into one unified physiological framework."}
            </p>
          </div>

          <div className="row">
            {inputData.map((item, index) => (
              <div className="col-lg-4 mb-4" key={index}>
                <div className="predyx2-card">
                  <div className="predyx2-card-number">{item.title}</div>
                  <div className="predyx2-card-content">
                    <p>{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="predyx2-bottom-box mt-4">
            <h3>Predyx is not built around tests.</h3>
            <p className="mb-0">It is built around biology.</p>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* DETERMINISTIC BY DESIGN - CMS ID 58 */}
      {/* ============================================ */}
      <section className="predyx3-section" style={{ background: "#fff" }}>
        <div className="container">
          <div className="predyx3-header text-center">
            <h2 className="predyx3-title mt-2">
              {parse(cms[58]?.cms_page_heading || "Deterministic <span>by Design</span>")}
            </h2>
            <p className="predyx3-description mt-2">
              {cms[58]?.cms_page_small_description || "Predyxiq does not generate conclusions through probability alone."}
            </p>
          </div>
          <div className="predyx3-timeline mt-5">
            {parse(cms[58]?.cms_page_description || "")}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* ORGAN SYSTEM TRAJECTORIES - CMS ID 59 */}
      {/* ============================================ */}
      <section className="predyx3-section" style={{ background: "#eaf0f6" }}>
        <div className="container">
          <div className="predyx3-header text-center">
            <h2 className="predyx3-title mt-2">
              {parse(cms[59]?.cms_page_heading || "")}
            </h2>
            <p className="predyx3-description mt-2">
              {cms[59]?.cms_page_small_description || ""}
            </p>
          </div>
          <div className="predyx3-timeline mt-5">
            {parse(cms[59]?.cms_page_description || "")}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* TRANSPARENT CLINICAL REASONING - CMS ID 60 */}
      {/* ============================================ */}
      <section className="predyx3-section" style={{ background: "#f9fcff" }}>
        <div className="container">
          <div className="predyx3-header text-center">
            <h2 className="predyx3-title mt-2">
              {parse(cms[60]?.cms_page_heading || "Transparent <span>Clinical Reasoning</span>")}
            </h2>
            <p className="predyx3-description mt-2">
              {cms[60]?.cms_page_small_description || "Every conclusion generated by Predyxiq can be traced back to the observations that contributed to it."}
            </p>
          </div>
          <div className="predyx3-timeline mt-5">
            {parse(cms[60]?.cms_page_description || "")}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* SUPPORT PHYSICIANS - CMS ID 61 */}
      {/* ============================================ */}
      <section className="predyx3-section" style={{ background: "#eaf0f6" }}>
        <div className="container">
          <div className="predyx3-header text-center">
            <h2 className="predyx3-title mt-2">
              {parse(cms[61]?.cms_page_heading || "Designed to <span>Support Physicians</span>")}
            </h2>
          </div>
          <div className="predyx3-timeline mt-5">
            {parse(cms[61]?.cms_page_description || "")}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* LIVING SCIENTIFIC PLATFORM - CMS ID 62 */}
      {/* ============================================ */}
      <section className="predyx3-section" style={{ background: "#f9fcff" }}>
        <div className="container">
          <div className="predyx3-header text-center">
            <h2 className="predyx3-title mt-2">
              {parse(cms[62]?.cms_page_heading || "A Living <span>Scientific Platform</span>")}
            </h2>
            <p className="predyx3-description mt-2">
              {cms[62]?.cms_page_small_description || "Medicine continues to evolve. New biomarkers emerge. Clinical evidence expands. Diagnostic technologies improve."}
            </p>
          </div>
          <div className="predyx3-timeline mt-5">
            {parse(cms[62]?.cms_page_description || "")}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* MORE THAN TECHNOLOGY - CMS ID 63 */}
      {/* ============================================ */}
      <section className="predyx3-section" style={{ background: "#eaf0f6" }}>
        <div className="container">
          <div className="predyx3-header text-center">
            <h2 className="predyx3-title mt-2">
              {parse(cms[63]?.cms_page_heading || "More <span>than Technology</span>")}
            </h2>
          </div>
          <div className="predyx3-timeline mt-5">
            {parse(cms[63]?.cms_page_description || "")}
          </div>
        </div>
      </section>

    
      {/* ============================================ */}
      {/* FOOTER CTA - CMS ID 64 */}
      {/* ============================================ */}
      <section className="funfacts" ref={ref}>
        <div className="overlay">
          <div className="container" style={{ textAlign: "center" }}>
            <h2 style={{ fontSize: "28px", color: "#fff", fontWeight: "600", marginBottom: "20px" }}>
              {parse(cms[64]?.cms_page_heading || "Clinical Intelligence should be understandable. Not mysterious.")}
            </h2>
            <p style={{ fontSize: "16px", color: "#fff", marginBottom: "30px" }}>
              {parse(cms[64]?.cms_page_small_description || "")}
            </p>
            {parse(cms[64]?.cms_page_description || "")}
          
          </div>
        </div>
      </section>
    </div>
  );
};

export default SciencePage;